import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HreflangTags from "./components/HreflangTags";
import LanguageRouter from "./components/LanguageRouter";
import { LANG_PREFIXES } from "./i18n/urls";
import { Toaster } from "./components/ui/toaster";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Divisions from "./components/Divisions";
import NumeriArabell from "./components/NumeriArabell";
import Footer from "./components/Footer";
import TrustpilotWidget, { isTrustpilotEnabled } from "./components/TrustpilotWidget";
import CookieModal from "./components/CookieModal";
import BackgroundAudio from "./components/BackgroundAudio";
import AnalyticsScripts from "./components/AnalyticsScripts";
import AccessModal from "./components/AccessModal";
import WhatsAppButton from "./components/WhatsAppButton";
import SplashScreen from "./components/SplashScreen";

// Pages — lazy loaded per ridurre il bundle iniziale (~70% in meno).
// Ogni route viene scaricata solo quando il visitatore ci naviga.
const SupercarPage = lazy(() => import("./pages/SupercarPage"));
const UrbanPage = lazy(() => import("./pages/UrbanPage"));
const CorporatePage = lazy(() => import("./pages/CorporatePage"));
const YachtsPage = lazy(() => import("./pages/YachtsPage"));
const AviationPage = lazy(() => import("./pages/AviationPage"));
const MembershipPage = lazy(() => import("./pages/MembershipPage"));
const TechnologyPage = lazy(() => import("./pages/TechnologyPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const RealEstatePage = lazy(() => import("./pages/RealEstatePage"));
const PrimeCarePage = lazy(() => import("./pages/PrimeCarePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const TermsPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const CookiePage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.CookiePage })));
const CancellationPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.CancellationPage })));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const AdminReviewsPage = lazy(() => import("./pages/AdminReviewsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ArabellMemberPage = lazy(() => import("./pages/ArabellMemberPage"));
const ArabellStaffPage = lazy(() => import("./pages/ArabellStaffPage"));
const StaffOfferPage = lazy(() => import("./pages/StaffOfferPage"));
const ContractPage = lazy(() => import("./pages/ContractPage"));
const RentGeneralConditionsPrint = lazy(() => import("./pages/RentGeneralConditionsPrint"));
const WallaaSafetyPage = lazy(() => import("./pages/WallaaSafetyPage"));
const SignContractPublic = lazy(() => import("./pages/SignContractPublic"));
const VehicleStockSheet = lazy(() => import("./pages/VehicleStockSheet"));
const LongTermPage = lazy(() => import("./pages/LongTermPage"));
const CargoPage = lazy(() => import("./pages/CargoPage"));
const SoccorsoStradalePage = lazy(() => import("./pages/SoccorsoStradalePage"));
const YuppiRentPage = lazy(() => import("./pages/YuppiRentPage"));
const KartooniaPage = lazy(() => import("./pages/KartooniaPage"));
const SalesContractPrint = lazy(() => import("./pages/SalesContractPrint"));
const VerifyContractPage = lazy(() => import("./pages/VerifyContractPage"));
const LocalLandingPage = lazy(() => import("./pages/LocalLandingPage"));

import { SERVICES } from "./data/seoLocations";
import ReviewsSection from "./components/ReviewsSection";
import SEO from "./components/SEO";

// Fallback minimal mentre la route viene scaricata.
const RouteLoading = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="font-display text-2xl tracking-[0.4em] text-arabell-gold opacity-60 animate-pulse">
      ARABELL
    </div>
  </div>
);

const Home = () => {
  const [accessOpen, setAccessOpen] = useState(false);
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    if (splashDone) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [splashDone]);

  const handleSplashEnd = () => {
    setSplashDone(true);
    document.body.style.overflow = "";
  };

  // Schema.org: Organization + WebSite + LocalBusiness (Knowledge Graph signals)
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://arabellgroup.com/#organization",
      name: "Arabell Group",
      alternateName: "Arabell Group SRL",
      url: "https://arabellgroup.com",
      logo: "https://arabellgroup.com/arabell-logo-512.png",
      image: "https://arabellgroup.com/og-arabell.jpg",
      description: "Gruppo italiano con sede a Bolzano specializzato in servizi premium: vendita veicoli premium, noleggio lungo termine anche con targa estera, noleggio breve termine di auto low-cost a prezzi competitivi anche senza carta di credito, supercar, charter jet privati e yacht, real estate di prestigio, Arabell Wallet e sviluppo di App social, programmi e applicazioni per nuove tecnologie.",
      foundingDate: "2024",
      founder: { "@type": "Person", name: "Luca Cassola" },
      slogan: "Move the world",
      telephone: "+39 352 0721132",
      email: "info@arabellgroup.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Via Marie Curie 17",
        addressLocality: "Bolzano",
        postalCode: "39100",
        addressRegion: "BZ",
        addressCountry: "IT",
      },
      areaServed: [
        { "@type": "Country", name: "Italia" },
        { "@type": "Place", name: "Europa" },
        { "@type": "Country", name: "Emirati Arabi Uniti" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Arabia Saudita" },
      ],
      sameAs: [
        "https://www.instagram.com/arabellgroup/",
        "https://www.linkedin.com/company/arabell-group/",
        "https://www.facebook.com/arabellgroup",
      ],
      brand: [
        { "@type": "Brand", name: "Yuppi-Rent", url: "https://yuppirent.com", description: "Noleggio auto a breve termine low cost, anche senza carta di credito" },
        { "@type": "Brand", name: "Social ADE", url: "https://ade-app.com", description: "Social network e app di community del gruppo" },
        { "@type": "Brand", name: "Wallaa", url: "https://wallaa.it", description: "Marketplace digitale di prodotti e servizi" },
        { "@type": "Brand", name: "Wallaa Safety", url: "https://arabellgroup.com/wallaa-safety", description: "App di sicurezza personale: SOS geolocalizzato, allerte in tempo reale e rete di contatti fidati" },
      ],
      knowsAbout: [
        "noleggio auto senza carta di credito",
        "noleggio auto low cost a breve termine",
        "noleggio a lungo termine con targa estera",
        "vendita supercar e auto premium",
        "charter jet privati e yacht",
        "immobili di prestigio",
        "sviluppo app e software",
      ],
      contactPoint: [{
        "@type": "ContactPoint",
        telephone: "+39 352 0721132",
        contactType: "customer service",
        availableLanguage: ["Italian", "English", "French", "Spanish", "German", "Arabic"],
        areaServed: ["IT", "EU", "AE", "QA", "SA"],
      }],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        bestRating: "5",
        worstRating: "1",
        ratingCount: "307",
        reviewCount: "307",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://arabellgroup.com/#website",
      url: "https://arabellgroup.com",
      name: "Arabell Group",
      publisher: { "@id": "https://arabellgroup.com/#organization" },
      inLanguage: ["it", "en", "fr", "es", "de", "ar"],
      potentialAction: {
        "@type": "SearchAction",
        target: "https://arabellgroup.com/recensioni?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://arabellgroup.com/#localbusiness",
      name: "Arabell Group SRL",
      image: "https://arabellgroup.com/og-arabell.jpg",
      url: "https://arabellgroup.com",
      telephone: "+39 352 0721132",
      email: "info@arabellgroup.com",
      priceRange: "€€€€",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Via Marie Curie 17",
        addressLocality: "Bolzano",
        postalCode: "39100",
        addressRegion: "BZ",
        addressCountry: "IT",
      },
      geo: { "@type": "GeoCoordinates", latitude: 46.4983, longitude: 11.3548 },
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "19:00",
      }],
    },
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      <SEO
        title="Arabell Group — Azienda specializzata in servizi premium"
        description="Arabell Group è un'azienda con sede a Bolzano specializzata in servizi premium: vendita di veicoli premium (nuovi e usati), noleggio a lungo termine anche con targa estera di supercar, auto d'élite e mezzi commerciali, noleggio a breve termine di auto low-cost a prezzi competitivi anche senza carta di credito, charter su misura di jet privati e yacht, real estate di prestigio, Arabell Wallet (custodia fiduciaria con cashback) e sviluppo di App social, programmi e applicazioni per nuove tecnologie. Servizi in Italia ed Europa."
        keywords="servizi premium, vendita veicoli premium, vendita auto nuove, vendita auto usate, noleggio lungo termine, noleggio breve termine, noleggio auto low cost, noleggio auto senza carta di credito, noleggio auto Bolzano, noleggio targa estera, supercar, auto d'élite, mezzi commerciali, charter jet privato, charter yacht, real estate di prestigio, Arabell Wallet, sviluppo app, sviluppo software, app social, programmi custom, nuove tecnologie, digital innovation, Arabell Group, Bolzano, Italia, Europa"
        path="/"
        jsonLd={homeJsonLd}
      />
      {!splashDone && <SplashScreen onFinish={handleSplashEnd} />}
      <Header onAccess={() => setAccessOpen(true)} />
      <Hero />
      <Divisions />
      <NumeriArabell />
      <ReviewsSection />
      {isTrustpilotEnabled() && (
        <section className="bg-[#0a0a0a] py-12 md:py-16 border-t border-white/5" data-testid="home-trustpilot-section">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <p className="text-[10px] tracking-[0.5em] uppercase text-arabell-gold mb-3">Recensioni verificate</p>
            <h2 className="font-display text-3xl md:text-4xl text-white tracking-[0.05em] mb-6">
              Quello che dicono i clienti Arabell
            </h2>
            <TrustpilotWidget variant="carousel" />
            <TrustpilotWidget variant="mini" className="mt-4" />
          </div>
        </section>
      )}
      <Footer />
      <WhatsAppButton />
      <CookieModal />
      <AccessModal open={accessOpen} onClose={() => setAccessOpen(false)} />
    </div>
  );
};

const PublicRoutes = () => (
  <Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route index element={<Home />} />
      <Route path="supercar-luxury" element={<SupercarPage />} />
      <Route path="cars" element={<SupercarPage />} />
      <Route path="urban-cars" element={<UrbanPage />} />
      <Route path="corporate-fleet" element={<CorporatePage />} />
      <Route path="yachts" element={<YachtsPage />} />
      <Route path="jets" element={<AviationPage />} />
      <Route path="aviation" element={<AviationPage />} />
      <Route path="membership" element={<MembershipPage />} />
      <Route path="credit-wallet" element={<WalletPage />} />
      <Route path="technology" element={<TechnologyPage />} />
      <Route path="real-estate" element={<RealEstatePage />} />
      <Route path="prime-care" element={<PrimeCarePage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="chi-siamo" element={<AboutPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="contatti" element={<ContactPage />} />
      <Route path="press" element={<PressPage />} />
      <Route path="blog" element={<BlogPage />} />
      <Route path="magazine" element={<BlogPage />} />
      <Route path="careers" element={<CareersPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="termini-e-condizioni" element={<TermsPage />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="privacy-policy" element={<PrivacyPage />} />
      <Route path="cookie-policy" element={<CookiePage />} />
      <Route path="cookie" element={<CookiePage />} />
      <Route path="cancellation-policy" element={<CancellationPage />} />
      <Route path="recensioni" element={<ReviewsPage />} />
      <Route path="reviews" element={<ReviewsPage />} />
      <Route path="admin" element={<AdminPage />} />
      <Route path="admin/ltd" element={<AdminPage forceOrg="ltd" />} />
      <Route path="admin/reviews" element={<AdminReviewsPage />} />
      <Route path="arabell-member" element={<ArabellMemberPage />} />
      <Route path="staff-offerta" element={<StaffOfferPage />} />
      <Route path="staff-contratto/:id" element={<ContractPage />} />
      <Route path="staff-condizioni-noleggio/:id" element={<RentGeneralConditionsPrint />} />
      <Route path="arabell-staff/*" element={<ArabellStaffPage />} />
      <Route path="long-term-de" element={<LongTermPage />} />
      <Route path="cargo" element={<CargoPage />} />
      <Route path="soccorso-stradale" element={<SoccorsoStradalePage />} />
      <Route path="noleggio-low-cost" element={<YuppiRentPage />} />
      <Route path="yuppi-rent" element={<YuppiRentPage />} />
      <Route path="wallaa-safety" element={<WallaaSafetyPage />} />
      <Route path="firma-contratto/:id" element={<SignContractPublic />} />
      <Route path="scheda-veicolo-stock" element={<VehicleStockSheet />} />
      <Route path="kartoonia" element={<KartooniaPage />} />
      <Route path="sales-contract-print/:id" element={<SalesContractPrint />} />
      <Route path="verify/:type/:id" element={<VerifyContractPage />} />
      <Route path=":slug" element={<LocalLandingPage />} />
      <Route path="*" element={<Home />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <LanguageRouter />
          <HreflangTags />
          <Routes>
            {/* URL localizzate: /en/..., /fr/..., /es/..., /de/..., /ar/... */}
            {LANG_PREFIXES.map((code) => (
              <Route key={code} path={`${code}/*`} element={<PublicRoutes />} />
            ))}
            {/* Italiano = default, senza prefisso */}
            <Route path="/*" element={<PublicRoutes />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
        <BackgroundAudio />
        <AnalyticsScripts />
      </div>
    </HelmetProvider>
  );
}

export default App;
